'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'
import StoryblokClient from 'storyblok-js-client'
const Storyblok = new StoryblokClient({ accessToken: process.env.NEXT_PUBLIC_SB_PREVIEW_TOKEN })

import { setDate } from '@/lib/date'
import { calculateReadingTime } from '@/lib/readingTime'
import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok'

import { PhnxArticle } from './Article'
import { ArticleProps } from './Article'

interface ArticleBlokProps {
  blok: {
    title: ArticleProps['title']
    featured_image: ArticleProps['featuredImage']
    crop_image?: boolean
    body: SbBlokData[]
    tags: ArticleProps['tags']
    date: ArticleProps['date']
    relatedArticles: Array<{
      full_slug: any
      content: any
      image: { src: string; alt: string; focus: string }
      title: string
      tags: string[]
      date: string
      timeToRead: string
      link: string
    }>
    author: string
  } & SbBlokData
  rels?: any
  name: string
  uuid: string
  storyID: string
  first_published_at: string
}

function ArticleReadTime(body: any) {
  if (body && !body[0]?.content) return '0 min read'
  if (!body) return '0 min read'
  const html = Storyblok.richTextResolver.render(body[0].content)
  return calculateReadingTime(html)
}

const Article: FC<ArticleBlokProps> = ({ blok, rels, storyID, first_published_at, name }) => {
  const [relatedArticlesFind, setRelatedArticles] = useState<
    ArticleBlokProps['blok']['relatedArticles']
  >([])
  const [authorRel, setAuthorRel] = useState<any>(rels)

  useEffect(() => {
    const author = blok.author
    if (author) {
      setAuthorRel(author)
    }
  }, [blok.author])

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      const articlesRaw = blok.relatedArticles || []

      const transformedArticles = articlesRaw
        .map((article) => {
          if (!article?.content) return null
          return {
            image: {
              src: article.content.featured_image?.filename || '',
              alt: article.content.featured_image?.alt || '',
              focus: article.content.featured_image?.focus || '',
            },
            title: article.content.title || '',
            tags: article.content.tags || [],
            date: setDate(article.content.date),
            timeToRead: ArticleReadTime(article.content.body),
            link: `/${article.full_slug}`,
          }
        })
        .filter((article): article is NonNullable<typeof article> => article !== null)
      if (transformedArticles.length > 0) {
        setRelatedArticles(
          transformedArticles.map((article) => ({
            ...article,
            full_slug: article.link.replace('/', ''),
            content: null,
          }))
        )
        return
      }

      const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'au'
      let filterLocation = location === 'en-nz' ? 'au' : `en-nz`

      const sbParams = {
        ...searchParamsData,
        filter_query: {
          component: { in: 'article' },
          __or: [
            { 'settings.location_available': { in: location } },
            { 'settings.location_available': { not_in: filterLocation } },
          ],
        },
        excluding_ids: storyID,
        sort_by: 'content.date:desc',
        per_page: 3,
      }

      const storyblokApi = getStoryblokApi()
      const { data } = await storyblokApi.get(`cdn/stories/`, sbParams)

      if (data?.stories) {
        const fallbackArticles = data.stories.map((story: any) => ({
          image: {
            src: story.content.featured_image?.filename || '',
            alt: story.content.featured_image?.alt || '',
            focus: story.content.featured_image?.focus || '',
          },
          title: story.content.title || '',
          tags: story.content.tags || [],
          date: setDate(story.content.date),
          timeToRead: ArticleReadTime(story.content.body),
          link: `/${story.full_slug}`,
        }))
        setRelatedArticles(fallbackArticles)
      }
    }

    fetchRelatedArticles()
  }, [blok._uid, blok.relatedArticles, rels])

  return (
    <div {...storyblokEditable(blok)}>
      <PhnxArticle
        title={blok.title}
        h1={name}
        featuredImage={
          blok.featured_image && {
            filename: blok.featured_image.filename,
            alt: blok.featured_image.alt,
            focus: blok.featured_image.focus,
          }
        }
        cropImage={blok.crop_image}
        author={
          authorRel && {
            title: authorRel.content?.title || '',
            name: authorRel.name || '',
            bio: authorRel.content?.bio
              ? authorRel.content.bio.map((child: SbBlokData) => (
                  <StoryblokServerComponent blok={child} key={child._uid} />
                ))
              : [],
            url: `/${authorRel.full_slug}`,
            image: {
              src: authorRel.content?.image?.filename || '',
              alt: authorRel.content?.image?.alt || '',
            },
          }
        }
        tags={blok.tags}
        date={setDate(first_published_at) || blok.date}
        relatedArticlesTitle={'Related Articles'}
        relatedArticlesCTA={{ label: 'View all articles', link: '/insights/search' }}
        relatedArticles={relatedArticlesFind}
      >
        {blok.body &&
          blok.body.map((child) => <StoryblokServerComponent blok={child} key={child._uid} />)}
      </PhnxArticle>
    </div>
  )
}

export default Article
